import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { logout } from "@/store/authSlice";
import { useActiveModule } from "@/hooks/useActiveModule";
import { moduleAdminPath } from "@/config/moduleDefaults";
import { resolveLogoutLoginPath } from "@/routes/ModuleScopeGuard";
import { UserAvatar } from "@/components/user/UserAvatar";
import { resolveProfileImageUrl } from "@/utils/fileUrl";
import { cn } from "@/utils/cn";

import type { ErpModule } from "@/api/platform.api";

interface ProfileMenuProps {
  variant?: "default" | "header";
}

function resolveProfilePath(
  masterAdmin?: boolean,
  activeModule?: ErpModule | null,
  entryModule?: ErpModule | null,
): string {
  if (masterAdmin) return "/master/profile";
  const module = activeModule ?? entryModule ?? "POS";
  return moduleAdminPath(module, "profile");
}

export function ProfileMenu({ variant = "default" }: ProfileMenuProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const activeModule = useActiveModule();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const avatarUrl = useMemo(
    () =>
      resolveProfileImageUrl(
        user?.profilePictureUrl,
        user?.userUuid,
        user?.profilePictureRevision,
      ) ?? null,
    [user?.profilePictureUrl, user?.userUuid, user?.profilePictureRevision],
  );

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const fullName = `${user.firstName} ${user.lastName ?? ""}`.trim();
  const profilePath = resolveProfilePath(user.masterAdmin, activeModule, entryModule);

  const handleLogout = () => {
    setOpen(false);
    const loginPath = resolveLogoutLoginPath({
      pathname: location.pathname,
      entryModule,
      activeModule,
      masterAdmin: user.masterAdmin,
    });
    void dispatch(logout());
    navigate(loginPath, { replace: true });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cn(
          variant === "header"
            ? "workspace-header-profile-btn"
            : "inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800",
        )}
        onClick={() => setOpen((value) => !value)}
        aria-label={t('labels.accountMenu')}
        aria-expanded={open}
      >
        <UserAvatar
          firstName={user.firstName}
          lastName={user.lastName}
          imageUrl={avatarUrl}
          size={variant === "header" ? "sm" : "md"}
        />
        {variant === "default" ? (
          <span className="hidden max-w-[10rem] truncate sm:inline">{fullName || user.email}</span>
        ) : null}
      </button>

      {open ? (
        <div
          className={cn(
            'absolute right-0 z-50 mt-2 w-56 animate-fade-in rounded-xl border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900',
            variant === 'header' && 'workspace-header-overlay',
          )}
        >
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-800">
            <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
              {fullName || user.email}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
          </div>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={() => {
              setOpen(false);
              navigate(profilePath);
            }}
          >
            <PersonIcon fontSize="small" />
            {t('profileMenu.myProfile')}
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-slate-700 transition hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
            onClick={() => handleLogout()}
          >
            <LogoutIcon fontSize="small" />
            {t('profileMenu.signOut')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
